import React, { useEffect, useRef, useState } from 'react';
import VideoInfoWrapper from '../components/Video/VideoInfoWrapper';
import VideoChatWrapper from '../components/Video/VideoChatWrapper';
import VideoPlayer from '../components/Video/VideoPlayer';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import VideoChatEmptyContent from '../components/Video/VideoChatEmptyContent';
import ModalWrapper from '../components/Modal/ModalWrapper';
import DownloadItemsWrapper from '../components/Common/DownloadItemsWrapper';
import { useTranslation } from 'react-i18next';
import useGetLocationData from '../hooks/useGetLocationData';
import useGetBeatInfo from '../hooks/useGetBeatInfo';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import useApi from '../hooks/useApi';
import { languageState } from '../recoil/language/languageAtom';
import LocalAlertDialog from '../components/Dialog/LocalAlertDialog';
import { loadingSpinnerState } from '../recoil/loadingSpinner/loadingSpinnerAtom';
import VideoDefaultScreen from '../components/Video/VideoDefaultScreen';
import 'videojs-overlay';
import dayjs from 'dayjs';
import UAParser from 'ua-parser-js';
import ERROR_CODES from '../constants/errorCodes';
import SCREEN_SIZES from '../constants/screenSizes';
import { liveStatusState } from '../recoil/chatCount/chatCountAtom';
import { playerIsPlayingState } from '../recoil/player/playerIsPlayingAtom';
import { countryState } from '../recoil/country/countryAtom';
import * as Sentry from '@sentry/react';

const VideoLiveDetail = () => {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isMobileScreen, setIsMobileScreen] = useState(window.innerWidth <= SCREEN_SIZES.MOBILE_SCREEN_WIDTH);
  const [clipInfo, setClipInfo] = useState(null);
  const [liveClipInfo, setLiveClipInfo] = useState(null);
  const playerRef = useRef(null);
  const [keyPairId, setKeyPairId] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [signature, setSignature] = useState(null);
  const { id } = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const accessToken = localStorage.getItem('access_token');
  const countryData = useRecoilValue(countryState);
  const navigate = useNavigate();
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const liveSubtitlesRef = useRef([]);
  let overlayOptions = [];
  const offsetTimestampRef = useRef(null);
  const activeLangRef = useRef('en');
  const selectedLanguage = useRecoilValue(languageState);
  const setIsLoading = useSetRecoilState(loadingSpinnerState);
  const liveStatus = useRecoilValue(liveStatusState); // streaming live_status 전역 구독중, 값 바뀌면 리렌더링
  const setPlayerIsPlayingState = useSetRecoilState(playerIsPlayingState);
  const [localAlertDialogConfig, setLocalAlertDialogConfig] = useState({
    isOpen: false,
    title: '',
    confirmText: '',
    handleConfirm: () => {},
  });

  const api = useApi();
  const apiV1 = useApi('v1');
  const apiV2 = useApi('v2');
  // const { showDialog } = useDialog();

  // 사파리 대응 코드 작성용
  const parser = new UAParser();
  const { browser } = parser.getResult();

  // 단순 로그인 확인용
  const { isLogin, hasAccessToken } = useGetBeatInfo(accessToken, countryData?.country);

  const queryParams = new URLSearchParams(location.search);
  const clipLiveId = queryParams.get('clip_live_id');

  let locationData = {
    location: 'seoul', // local 환경에서 기본값
  };

  // 배포 환경에서만 실행(local 환경에서 cors error)
  if (process.env.NODE_ENV === 'production') {
    locationData = useGetLocationData('https://location.mubeat.tv/');
  }

  const videoSources = clipInfo?.clipList?.map((clip) => ({
    ...clip,
    src: `${clip.url}?Policy=${policy}&Signature=${signature}&Key-Pair-Id=${keyPairId}`,
    // src: clip.url, // signed cookie일 때 사용
    type: 'application/x-mpegURL',
    withCredentials: false,
  }));

  const videoOptions = {
    // autoplay: true,
    preload: true,
    controls: true,
    responsive: true,
    fluid: true,
    liveui: true,
    html5: {
      vhs: {
        overrideNative: true,
      },
      // nativeAudioTracks: false,
      // nativeVideoTracks: false,
    },
    controlBar: {
      pictureInPictureToggle: false,
      remainingTimeDisplay: false,
      volumePanel: {
        inline: true,
      },
      currentTimeDisplay: true,
      durationDisplay: true,
      qualitySelector: true,
    },
    // 단순 테스트 url
    // sources: [
    //   {
    //     src: 'https://bitdash-a.akamaihd.net/content/sintel/hls/playlist.m3u8',
    //     type: 'application/x-mpegURL',
    //   },
    // ],
    sources: videoSources,
  };

  const clearSession = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('exp');
    Sentry.setUser(null);
  };

  const showLocalAlertDialog = (errorCode, newTargetId) => {
    let config;
    switch (errorCode) {
      case ERROR_CODES.exclusiveAccessForPurchasers:
        config = {
          title: t('not_purchased_state_msg'),
          confirmText: t('confirm'),
          handleConfirm: () => navigate(`/product-video-detail/${newTargetId}`),
        };
        break;
      case ERROR_CODES.notFoundVideo:
        config = {
          title: t('video_clip_not_found_msg'),
          confirmText: t('confirm'),
          handleConfirm: () => hideLocalAlertDialog(),
        };
        break;
      default:
        config = {
          title: t('video_clip_not_found_msg'),
          confirmText: t('confirm'),
          handleConfirm: () => hideLocalAlertDialog(),
        };
        break;
    }
    setLocalAlertDialogConfig({ ...config, isOpen: true });
  };
  const hideLocalAlertDialog = () => {
    setLocalAlertDialogConfig((prevConfig) => ({ ...prevConfig, isOpen: false }));
  };

  const handlePlayerReady = (player) => {
    playerRef.current = player;

    // player.on('waiting', () => {
    //   // videojs.log('waiting');
    // });

    player.on('playing', () => {
      setPlayerIsPlayingState(true);
    });

    player.on('pause', () => {
      setPlayerIsPlayingState(false);
    });

    player.on('ended', () => {
      setPlayerIsPlayingState(false);
    });

    player.on('dispose', () => {
      setPlayerIsPlayingState(false);
    });

    const { startFetching, stopFetching } = fetchControlSubtitle();

    const trackChangeHandler = () => {
      const activeTracks = player.textTracks();
      // let activeLang = 'en'; // 기본값 en
      for (let i = 0; i < activeTracks.length; i++) {
        const track = activeTracks[i];

        // trackMode가 disabled 일 때 자막 없음
        if (track.mode === 'disabled') {
          activeLangRef.current = '';
        }

        if (track.kind === 'subtitles' && track.mode === 'showing') {
          // 현재 활성화된 자막 트랙을 처리
          activeLangRef.current = track.language;
          break;
        }
      }
    };

    // player.on('waiting', () => {
    //   videojs.log('player waiting');
    // });

    player.on('playing', () => {
      startFetching(); // 재생 중일 때 자막 페칭 시작
    });

    player.on('pause', () => {
      stopFetching(); // 플레이어 중지 시 자막 페칭 중지
    });

    player.on('dispose', () => {
      stopFetching(); // 플레이어 dispose 자막 페칭 중지
    });

    player.on('texttrackchange', () => {
      trackChangeHandler();
    });

    // 사파리 대응 코드
    if (browser.name === 'Safari') {
      player.on('loadedmetadata', () => {
        player.textTracks().addEventListener('change', trackChangeHandler);
      });
    }
  };

  // fetch된 자막 데이터를 기반으로 overlays를 업데이트하는 함수
  const updateOverlays = (subtitles, offset_timestamp) => {
    const currentTime = dayjs(); // 현재 시간
    const currentLiveTime = playerRef.current.liveTracker.liveCurrentTime();
    const currentPlayTime = playerRef.current.currentTime();
    const networkDelay = currentLiveTime - currentPlayTime; // 딜레이

    if (playerRef.current) {
      const newOverlaysOptions = subtitles.map((subtitle, index) => {
        // offset_timestamp가 ms 단위라 ms로 합산(api response값에 3초씩 추가)
        const startTime = dayjs(subtitle.start).add(offset_timestamp, 'millisecond');
        const endTime = dayjs(subtitle.end).add(offset_timestamp, 'millisecond');

        // 현재 시각, 네트워크 지연 등을 고려한 자막 노출 시간 계산
        const startOffset = startTime.subtract(networkDelay, 'second');
        const endOffset = endTime.subtract(networkDelay, 'second');

        return {
          id: subtitle.start, // filter 구분용 고유값
          content: subtitle.content,
          start: currentPlayTime + (startOffset.unix() - currentTime.unix()),
          end: currentPlayTime + (endOffset.unix() - currentTime.unix()),
          class: `custom-overlay-subtitle custom-overlay-subtitle-${index}`,
          align: 'bottom',
        };
      });

      // 중복값 제거
      const uniqueOverlaysOptions = newOverlaysOptions.filter(
        (newOverlay) => !overlayOptions.some((existingOverlay) => existingOverlay.id === newOverlay.id),
      );

      overlayOptions = [...overlayOptions, ...uniqueOverlaysOptions];

      playerRef.current.overlay({
        overlays: overlayOptions,
      });
    }
  };

  // 신규자막이 없는 경우 별도의 업데이트 막는 용도(videojs-overlay 업데이트 시 깜빡임 대응)
  const compareSameSubtitles = (prevSubtitles, newSubtitles) => {
    if (prevSubtitles.length !== newSubtitles.length) return false;

    for (let i = 0; i < newSubtitles.length; i++) {
      if (newSubtitles[i].id !== prevSubtitles[i]?.id) {
        return false;
      }
    }

    return true; // 배열 길이가 다르고 모든 id가 동일하고 true
  };

  const fetchLiveSubtitle = async () => {
    // let currentTime = new Date();
    // // 최초 호출에만 실행
    // if (offsetTimestampRef.current === null) {
    //   currentTime = new Date(currentTime.getTime() - 3000);
    // } else {
    //   console.log('offsetTimestampRef.current', offsetTimestampRef.current);
    //   currentTime = new Date(currentTime.getTime() - offsetTimestampRef.current);
    // }

    let currentTime = dayjs();
    // // 최초 호출에만 실행
    if (offsetTimestampRef.current === null) {
      currentTime = currentTime.subtract(4000, 'millisecond');
    } else {
      currentTime = currentTime.subtract(offsetTimestampRef.current, 'millisecond');
    }

    const currentTimeISO = currentTime.toISOString();

    // 사용자 선택에 따라 라이브 자막 언어 변경
    const lang = activeLangRef.current;

    if (countryData && countryData.country) {
      try {
        const response = await apiV2.get(
          `clip/live/${id}/subtitle?language=${lang}&playback_timestamp=${currentTimeISO}`,
          {
            headers: {
              Authorization: accessToken,
            },
          },
        );

        const offset_timestamp = response?.data?.offset_timestamp;
        const supported_languages = response?.data?.supported_languages;
        // 비교 로직 없을 때 기존 로직
        // liveSubtitlesRef.current = response?.data?.subtitles;

        const newSubtitles = response?.data?.subtitles;
        const prevSubtitles = liveSubtitlesRef.current;

        // 라이브 자막 버튼 생성(최초 호출에만 실행)
        if (offsetTimestampRef.current === null) {
          supported_languages.forEach((language) => {
            let label;
            switch (language) {
              case 'ja':
                label = '日本語';
                break;
              case 'zh-CN':
                label = '中文(简体)';
                break;
              case 'es':
                label = 'Español';
                break;
              case 'en':
                label = 'English';
                break;
              default:
                label = '한국어';
            }

            playerRef.current.addRemoteTextTrack(
              {
                label: label,
                kind: 'subtitles',
                language: language,
                mode: 'disabled',
              },
              false, // 이 두 번째 인자가 false이면 트랙이 자동으로 활성화되지 않음
            );
          });
        }

        // api 호출 시 버그 확인으로 offset_timestamp 프론트 내 조정
        // 호출 시에만 적용, start, end 등 하단 로직에는 기존 offset_timestamp 적용
        offsetTimestampRef.current = offset_timestamp + 1000;

        // 비교 로직 없을 때 기존 로직
        // updateOverlays(liveSubtitlesRef.current, offset_timestamp);

        if (compareSameSubtitles(prevSubtitles, newSubtitles)) {
          return;
        }
        updateOverlays(newSubtitles, offset_timestamp);
      } catch (error) {
        console.log(error);
      }
    }
  };

  const fetchControlSubtitle = () => {
    let fetchInterval;
    let overlayResetInterval;

    const startFetching = () => {
      // 이미 페칭 중이면 이전 인터벌 클리어
      if (fetchInterval) clearInterval(fetchInterval);
      // 이미 초기화 중이면 이전 인터벌 클리어
      if (overlayResetInterval) clearInterval(overlayResetInterval);

      // 2초마다 신규 자막 페칭
      fetchInterval = setInterval(() => {
        fetchLiveSubtitle();
      }, 2000);

      // 이미 주입된 자막 배열(overlayOptions) 초기화
      overlayResetInterval = setInterval(() => {
        overlayOptions = [];
      }, 60000); // 1분마다 실행
    };

    const stopFetching = () => {
      if (fetchInterval) clearInterval(fetchInterval);
      if (overlayResetInterval) clearInterval(overlayResetInterval);
    };

    return { startFetching, stopFetching };
  };

  useEffect(() => {
    return () => {
      // 플레이어 dispose로 메모리 누수 방지
      if (playerRef.current) {
        playerRef.current.dispose();
      }
    };
  }, []);

  // params.id값 바뀌면 리랜더링
  useEffect(() => {}, [id]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileScreen(window.innerWidth <= SCREEN_SIZES.MOBILE_SCREEN_WIDTH);
    };

    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  //   // 모바일에선 재생 불가 처리(다운로드 모달 출력)
  useEffect(() => {
    if (isMobileScreen) {
      setIsDownloadModalOpen(true);
    } else {
      setIsDownloadModalOpen(false);
    }
  }, [isMobileScreen]);

  useEffect(() => {
    const getVideoInfo = async () => {
      setIsLoading(true);
      setIsLocalLoading(true);

      const lang = selectedLanguage === 'ko' ? 'KO' : 'EN';

      if (countryData && countryData.country) {
        try {
          const response = await apiV1.get(
            `clip/play/${id}?country=${countryData?.country}&location=${locationData?.location}&lang=${lang}`,
            {
              headers: {
                Authorization: accessToken,
              },
            },
          );

          setClipInfo(response?.data);

          const {
            ['CloudFront-Key-Pair-Id']: keyPairId,
            ['CloudFront-Policy']: policy,
            ['CloudFront-Signature']: signature,
          } = response.data.cookieList;

          setKeyPairId(keyPairId);
          setPolicy(policy);
          setSignature(signature);

          // // 쿠키 세팅
          // document.cookie = `CloudFront-Key-Pair-Id=${keyPairId}; path=/; domain=localhost`;
          // document.cookie = `CloudFront-Policy=${policy}; path=/; domain=localhost`;
          // document.cookie = `CloudFront-Signature=${signature}; path=/; domain=localhost`;
        } catch (error) {
          if (error.code === 'ERR_NETWORK' || error.code === 'ERR_BAD_RESPONSE') {
            navigate('/error');
          }

          const errorCode = error?.response && error?.response.data && error?.response.data.code;
          if (errorCode === ERROR_CODES.exclusiveAccessForPurchasers) {
            const newTargetId = error?.response?.data?.data?.targetIds[0];
            showLocalAlertDialog(ERROR_CODES.exclusiveAccessForPurchasers, newTargetId);
          }
          if (errorCode === ERROR_CODES.notFoundVideo) {
            showLocalAlertDialog(ERROR_CODES.notFoundVideo);
          } else {
            console.log(errorCode);
          }
        } finally {
          setIsLoading(false);
          setIsLocalLoading(false);
        }
      }
    };

    if (hasAccessToken === false || isLogin === false) {
      clearSession();
      navigate('/');
    }

    if (isLogin === true) getVideoInfo();
  }, [countryData, id, isLogin, liveStatus, hasAccessToken]); // liveStatus 변경되면 getVideoInfo 재호출 및 리렌더링

  useEffect(() => {
    const getLiveClipInfo = async () => {
      try {
        const response = await api.get(`clip/live/${clipLiveId}`, {
          headers: {
            Authorization: accessToken,
          },
        });
        setLiveClipInfo(response?.data);
      } catch (error) {
        if (error.code === 'ERR_NETWORK' || error.code === 'ERR_BAD_RESPONSE') {
          navigate('/error');
        } else {
          console.log(error);
        }
      }
    };

    if (isLogin === true) getLiveClipInfo();
  }, [clipLiveId, isLogin, hasAccessToken]);

  return (
    <>
      {isLogin ? (
        <>
          <div className={'ml-page pt-80 ml-video-live-detail-page'}>
            <div className='page-inner'>
              <div className='layout-inner'>
                <div className='ml-video-detail-page-wrapper'>
                  {!isMobileScreen ? (
                    <div className='ml-video-layout-wrapper'>
                      <div className='ml-video-left-wrapper'>
                        {isLocalLoading ? (
                          <VideoDefaultScreen />
                        ) : (
                          <VideoPlayer
                            options={videoOptions}
                            onReady={handlePlayerReady}
                            videoType={'live'}
                            policy={policy}
                            keyPairId={keyPairId}
                            signature={signature}
                            clipInfo={clipInfo}
                            liveClipInfo={liveClipInfo}
                            id={id}
                            countryData={countryData}
                          />
                        )}

                        <VideoInfoWrapper videoType={'live'} clipInfo={clipInfo} liveClipInfo={liveClipInfo} id={id} />
                      </div>
                      <div className='ml-video-right-wrapper'>
                        {clipInfo?.live_status === 'end' ? <VideoChatEmptyContent /> : <VideoChatWrapper id={id} />}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <ModalWrapper
            isOpen={isDownloadModalOpen}
            size={'sm'}
            noScroll={true}
            onClose={() => {
              navigate(-1);
            }}
          >
            <DownloadItemsWrapper title={t('mubeat_download_msg')} />
          </ModalWrapper>
          {/* 전역관리의 dialog 대신 별도의 로컬 alert dialog 사용*/}
          {localAlertDialogConfig.isOpen ? (
            <LocalAlertDialog
              title={localAlertDialogConfig.title}
              confirmText={localAlertDialogConfig.confirmText}
              handleConfirm={localAlertDialogConfig.handleConfirm}
            />
          ) : null}
        </>
      ) : null}
    </>
  );
};

export default VideoLiveDetail;
